import type { PayType } from '@/app/features/employee/emp-payroll';
import dayjs, { type Dayjs } from 'dayjs';
import type { Ref } from 'react';

/**
 * Contract between the page and its configuration panels.
 *
 * The page owns one edit mode and one Save. It drives every panel through this
 * handle, so it never has to know what an individual panel contains.
 *
 * `validate` and `save` both answer with `null` when all is well, or a short
 * sentence explaining what went wrong.
 */
export interface ConfigPanelHandle {
  /** Checks the panel's own form. Nothing is sent while this reports a problem. */
  validate: () => Promise<string | null>;
  /** Sends the panel's payload. Never throws, always answers. */
  save: () => Promise<string | null>;
  /** Drops unsaved edits and restores the last values the server returned. */
  reset: () => void;
}

export type ConfigPanelProps = {
  /** Which population this panel configures. */
  payType: PayType;
  /** Page level edit mode. Panels are read only while this is false. */
  isEditing: boolean;
  ref?: Ref<ConfigPanelHandle>;
};

/**
 * Name of the grade `Form.List`. Shared because the benefits drawer writes back
 * into the grade form by path.
 */
export const GRADE_LIST = 'grades';

/** How dates read on this page: 01 Sep 2026, not 2026-09-01. */
export const DATE_DISPLAY = 'DD MMM YYYY';

/**
 * Blocks every day before the effective date already stored.
 *
 * The effective date marks when a version starts applying, so a new one may
 * only be the same day or later — moving it backwards would rewrite payroll
 * that has already run. Returns `undefined` when nothing is stored yet, which
 * leaves the picker fully open for a first setup.
 */
export const disabledBeforeSaved = (saved?: string | null) => {
  if (!saved) return undefined;
  const floor = dayjs(saved).startOf('day');
  return (current: Dayjs) => Boolean(current && current.isBefore(floor, 'day'));
};

/**
 * The theme sizes a middle control at 38px with a 6px radius. Custom controls on
 * this page reuse these so a pill, a select and an icon button line up exactly.
 */
export const CONTROL = 'h-[38px] rounded-md';
